import React, { useState } from 'react';
import { Link } from 'gatsby';
import { Dialog, Disclosure } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import SorryLogo from "../images/sorry_logo_dark.svg";
import SorryLogoLight from "../images/sorry-logo-light.svg";
import NavigationFlyout from './flyout-navigation';

const pages = [
  { name: 'Public Status Pages', href: '/public-status-page' },
  { name: 'Private Status Pages', href: '/private-status-page' },
  { name: 'Status Badge', href: '/integrations/status-badge/' },
  { name: 'Status API', href: '/status-api' },
]

const usecases = [
  { name: 'Incidents', href: '/notices/incidents' },
  { name: 'Maintenance', href: '/notices/maintenance' },
  { name: 'Change Log', href: '/notices/general' },
]

const recentPosts = [
  {
    title: 'Incident communication 101',
    href: '/incident-communications-101/',
  },
  {
    title: 'Why you need a status page',
    href: '/blog/why-you-need-a-status-page/',
  },
]

const Navigation = () => {
  const[mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="z-10 sticky top-0 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-x-6 p-6 lg:px-8" aria-label="Global">
        <div className="flex">
          <Link to="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Sorry App</span>
            <img className="h-8 w-auto" src={SorryLogo} alt="" />
          </Link>
        </div>

        <div className="hidden lg:flex lg:gap-x-8">
          <div className="ml-8">
            <NavigationFlyout />
          </div>
          <Link to="/integrations" className="text-sm font-semibold leading-6 text-gray-900">
            Integrations
          </Link>
          <Link to="/pricing" className="text-sm font-semibold leading-6 text-gray-900">
            Pricing
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-end gap-x-6">
          <a href="https://app.sorryapp.com/people/sign_in" target="_blank" rel="noreferrer" className="hidden lg:block lg:text-sm lg:font-medium lg:leading-6 lg:text-gray-900 hover:font-semibold">
            <img src="https://status.sorryapp.com/badge.svg" alt="Icon showing our current status." className="h-6 w-6 float-right ml-1" /> Log in
          </a>
          <a
            href="https://app.sorryapp.com/people/sign_up?plan=price_0QSH3a596061iK2wzf4dPxQ4&subscribers=1000&pages=1&privacy=false"
            className="hidden lg:block rounded-md bg-white px-2.5 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-400 hover:bg-gray-50"
          >
            Start Free Trial
          </a>
          <Link
            to="/demo"
            className="hidden lg:block rounded-md bg-gray-900 px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            Schedule a Demo
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-900"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      <Dialog as="div" className="lg:hidden" open={mobileMenuOpen} onClose={setMobileMenuOpen}>
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-gray-900 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center gap-x-6">
            <Link to="/" className="-m-1.5 p-1.5 w-3/4">
              <span className="sr-only">Sorry App</span>
              <img
                className="h-8 sm:hidden"
                src={SorryLogoLight}
                alt=""
              />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-gray-100 w-1/4"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6 float-right" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                <Disclosure as="div" className="-mx-3">
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                        Status Pages
                        <ChevronDownIcon
                          className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="mt-2 space-y-2">
                        {pages.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-white hover:bg-gray-800"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                <Disclosure as="div" className="-mx-3">
                  {({ open }) => (
                    <>
                      <Disclosure.Button className="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                        Use Cases
                        <ChevronDownIcon
                          className={`h-5 w-5 flex-none ${open ? 'rotate-180' : ''}`}
                          aria-hidden="true"
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="mt-2 space-y-2">
                        {usecases.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="block rounded-lg py-2 pl-6 pr-3 text-sm font-semibold leading-7 text-white hover:bg-gray-800"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>

                {recentPosts.map((post) => (
                  <Link
                    key={post.title}
                    to={post.href}
                    className="-mx-3 block rounded-lg py-2 px-3 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                  >
                    {post.title}
                  </Link>
                ))}

                <Link to="/integrations" className="-mx-3 block rounded-lg py-2 px-3 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                  Integrations
                </Link>
                <Link to="/pricing" className="-mx-3 block rounded-lg py-2 px-3 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                  Pricing
                </Link>
              </div>
              <div className="py-6">
                <Link
                  to="https://app.sorryapp.com/people/sign_in" target="_blank" rel="noreferrer"
                  className="-mx-3 block rounded-lg py-2.5 px-3 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                >
                  <img src="https://status.sorryapp.com/badge.svg" alt="Icon showing our current status." className="h-6 w-6 float-right ml-1" /> Log in
                </Link>
                <Link
                  to="https://app.sorryapp.com/people/sign_up?plan=price_0QSH3a596061iK2wzf4dPxQ4&subscribers=1000&pages=1&privacy=false"
                  className="-mx-3 block my-3 bg-white rounded-lg py-2.5 px-3 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="/demo"
                  className="-mx-3 block bg-gray-900 my-3 rounded-lg py-2.5 px-3 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                >
                  Schedule a Demo
                </Link>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
};

export default Navigation;